import type { TypedDocumentNode } from '@graphql-typed-document-node/core';
import { createInfiniteQuery, useQueryClient } from '@tanstack/svelte-query';
import type { GraphQLClient, Variables } from 'graphql-request';
import { getQuery } from '../../../query-utils/getQuery';
import { getQueryKeys } from '../../../query-utils/getQueryKeys';

export interface InfiniteQueryOptions<T, V extends Variables> {
	gqlClient: GraphQLClient;
	document: TypedDocumentNode<T, V>;
	headers?: { Authorization: string };
	variables?: V;
	transformOffset?: (offset: number) => Record<string, unknown>;
	limit: number;
	maxItems?: number;
	initialOffset?: number;
	queryFnOverride?: (v: Variables) => Promise<T>;
}

export function infiniteQuery<T, V extends Variables>({
	gqlClient,
	document,
	variables,
	headers,
	queryFnOverride,
	transformOffset = (offset: number) => ({ offset }),
	limit,
	maxItems = 0,
	initialOffset = 0
}: InfiniteQueryOptions<T, V>) {
	if (!queryFnOverride) {
		const queryClient = useQueryClient();

		queryClient.fetchInfiniteQuery({
			...getQuery(gqlClient, document, variables),
			initialPageParam: initialOffset
		});
	}

	return createInfiniteQuery(() => ({
		queryKey: getQueryKeys(document, variables),
		initialPageParam: initialOffset,
		queryFn: (data) => {
			const offset = data.pageParam;
			const offsetVariables = transformOffset(offset);

			if (queryFnOverride) {
				const overrideData = queryFnOverride({
					...variables,
					...offsetVariables
				} as Variables);

				if (overrideData) {
					return new Promise((r) => {
						r(overrideData);
					}) as Promise<T>;
				}
			}

			return gqlClient.request(
				document,
				{
					...variables,
					...offsetVariables
				} as Variables,
				headers
			);
		},
		getNextPageParam: (lastPage, pages) => {
			let nextOffset: number | undefined = 0;

			if (lastPage && pages) {
				nextOffset = limit * pages.length + initialOffset;

				if (maxItems && nextOffset > maxItems) {
					nextOffset = undefined;
				}
			}

			return nextOffset;
		}
	}));
}
